/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { AdminInvestmentToken } from './AdminInvestmentToken';
import {
    AdminInvestmentTokenFromJSON,
    AdminInvestmentTokenFromJSONTyped,
    AdminInvestmentTokenToJSON,
} from './AdminInvestmentToken';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { InvestmentCancellationEnum } from './InvestmentCancellationEnum';
import {
    InvestmentCancellationEnumFromJSON,
    InvestmentCancellationEnumFromJSONTyped,
    InvestmentCancellationEnumToJSON,
} from './InvestmentCancellationEnum';
import type { FrontendStatusEnum } from './FrontendStatusEnum';
import {
    FrontendStatusEnumFromJSON,
    FrontendStatusEnumFromJSONTyped,
    FrontendStatusEnumToJSON,
} from './FrontendStatusEnum';
import type { InvestmentAcquisitionTypeRecipientEnum } from './InvestmentAcquisitionTypeRecipientEnum';
import {
    InvestmentAcquisitionTypeRecipientEnumFromJSON,
    InvestmentAcquisitionTypeRecipientEnumFromJSONTyped,
    InvestmentAcquisitionTypeRecipientEnumToJSON,
} from './InvestmentAcquisitionTypeRecipientEnum';

/**
 * A summary representation of an investment, optimized for administrative list views such as approvals, incoming payments, and digital asset tracking.
 * @export
 * @interface AdminInvestmentList
 */
export interface AdminInvestmentList {
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentList
     */
    readonly id: string;
    /**
     * 
     * @type {InvestmentAcquisitionTypeRecipientEnum}
     * @memberof AdminInvestmentList
     */
    readonly acquisitionType: InvestmentAcquisitionTypeRecipientEnum;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentList
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentList
     */
    readonly distributionPlatformDisplayName: string;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestmentList
     */
    readonly fulfilledAmount: number;
    /**
     * 
     * @type {FrontendStatusEnum}
     * @memberof AdminInvestmentList
     */
    readonly frontendStatus: FrontendStatusEnum | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentList
     */
    readonly investmentTotal: Money;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentList
     */
    readonly investmentTotalFulfilled: Money | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentList
     */
    readonly investorName: string;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentList
     */
    readonly paymentCode: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvestmentList
     */
    readonly paymentDeadline: Date | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvestmentList
     */
    readonly paymentTotal: Money;
    /**
     * 
     * @type {string}
     * @memberof AdminInvestmentList
     */
    readonly productName: string;
    /**
     * 
     * @type {number}
     * @memberof AdminInvestmentList
     */
    readonly remainingAmount: number;
    /**
     * the date the investor made the investment offer.
     * @type {Date}
     * @memberof AdminInvestmentList
     */
    readonly signedAt: Date;
    /**
     * 
     * @type {AdminInvestmentToken}
     * @memberof AdminInvestmentList
     */
    readonly token: AdminInvestmentToken;
    /**
     * This field only indicates if the action can be done, not if it can be done by the requesting user.
     * @type {Array<InvestmentCancellationEnum>}
     * @memberof AdminInvestmentList
     */
    readonly canCancel: Array<InvestmentCancellationEnum>;
    /**
     * This field only indicates if the action can be done, not if it can be done by the requesting user.
     * @type {boolean}
     * @memberof AdminInvestmentList
     */
    readonly canIssue: boolean;
}



/**
 * Check if a given object implements the AdminInvestmentList interface.
 */
export function instanceOfAdminInvestmentList(value: object): value is AdminInvestmentList {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('acquisitionType' in value) || value['acquisitionType'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('distributionPlatformDisplayName' in value) || value['distributionPlatformDisplayName'] === undefined) return false;
    if (!('fulfilledAmount' in value) || value['fulfilledAmount'] === undefined) return false;
    if (!('frontendStatus' in value) || value['frontendStatus'] === undefined) return false;
    if (!('investmentTotal' in value) || value['investmentTotal'] === undefined) return false;
    if (!('investmentTotalFulfilled' in value) || value['investmentTotalFulfilled'] === undefined) return false;
    if (!('investorName' in value) || value['investorName'] === undefined) return false;
    if (!('paymentCode' in value) || value['paymentCode'] === undefined) return false;
    if (!('paymentDeadline' in value) || value['paymentDeadline'] === undefined) return false;
    if (!('paymentTotal' in value) || value['paymentTotal'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('signedAt' in value) || value['signedAt'] === undefined) return false;
    if (!('token' in value) || value['token'] === undefined) return false;
    if (!('canCancel' in value) || value['canCancel'] === undefined) return false;
    if (!('canIssue' in value) || value['canIssue'] === undefined) return false;
    return true;
}

export function AdminInvestmentListFromJSON(json: any): AdminInvestmentList {
    return AdminInvestmentListFromJSONTyped(json, false);
}

export function AdminInvestmentListFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminInvestmentList {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'acquisitionType': InvestmentAcquisitionTypeRecipientEnumFromJSON(json['acquisition_type']),
        'createdAt': (new Date(json['created_at'])),
        'distributionPlatformDisplayName': json['distribution_platform_display_name'],
        'fulfilledAmount': json['fulfilled_amount'],
        'frontendStatus': FrontendStatusEnumFromJSON(json['frontend_status']),
        'investmentTotal': MoneyFromJSON(json['investment_total']),
        'investmentTotalFulfilled': MoneyFromJSON(json['investment_total_fulfilled']),
        'investorName': json['investor_name'],
        'paymentCode': json['payment_code'],
        'paymentDeadline': (json['payment_deadline'] == null ? null : new Date(json['payment_deadline'])),
        'paymentTotal': MoneyFromJSON(json['payment_total']),
        'productName': json['product_name'],
        'remainingAmount': json['remaining_amount'],
        'signedAt': (new Date(json['signed_at'])),
        'token': AdminInvestmentTokenFromJSON(json['token']),
        'canCancel': ((json['can_cancel'] as Array<any>).map(InvestmentCancellationEnumFromJSON)),
        'canIssue': json['can_issue'],
    };
}

export function AdminInvestmentListToJSON(value?: Omit<AdminInvestmentList, 'id'|'acquisition_type'|'created_at'|'distribution_platform_display_name'|'fulfilled_amount'|'frontend_status'|'investment_total'|'investment_total_fulfilled'|'investor_name'|'payment_code'|'payment_deadline'|'payment_total'|'product_name'|'remaining_amount'|'signed_at'|'token'|'can_cancel'|'can_issue'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
    };
}

